/**
 * RegulaLegalIdVerification — orchestrator.
 *
 * Mirrors LegalIdVerification.tsx (Veriff) but drives athena's REGULA UPLOAD model via
 * useRegulaLegalIdVerification: the document is captured client-side (Regula locator, via
 * useRegulaEvents) and the uncropped base64 images are POSTed, then polled to a terminal outcome.
 *
 * States: initializing → ready (intro) → capturing (Regula locator) → processing (submit + poll)
 *         → confirming (reused ConfirmationScreen) → completed | error.
 *
 * Renders inside the Regula iframe; the hook completes the IDME_READY/IDME_INIT handshake and the
 * VERIFICATION_COMPLETE / VERIFICATION_ERROR postMessage protocol (same contract as Veriff).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRegulaLegalIdVerification, useRegulaEvents } from "../../hooks/regulaLegalIdVerification";
import { logger } from "../../shared/utils/logger";
import { toRegulaLocale } from "../../sdk/regulaLocale";
// Reuse the exact Veriff screens (vendor-neutral) + page styling.
import { IntroScreen } from "../legalIdVerification/screens/IntroScreen";
import { ProcessingScreen } from "../legalIdVerification/screens/ProcessingScreen";
import { ConfirmationScreen } from "../legalIdVerification/screens/ConfirmationScreen";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";
import { initAnalytics, setAnalyticsLocale, reportAnalyticsSetupFailure } from "../../shared/analytics/analyticsClient";
import { trackViewed, trackClicked } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../shared/analytics/documentCaptureEvents";
import { CaptureScreen } from "./screens/CaptureScreen";

export function RegulaLegalIdVerification() {
  const { t, i18n } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [showSpinner, setShowSpinner] = useState(true);
  // True from entering "capturing" until the Regula SDK has loaded and mounted its own UI.
  const [sdkLoading, setSdkLoading] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  const {
    state,
    error,
    verificationId,
    verificationResult,
    fallbackAvailable,
    setState,
    initializeRegulaLegalIdVerification,
    submitCapture,
    confirmVerification,
    handleVerificationError,
  } = useRegulaLegalIdVerification();

  // Set up analytics transport + session once for this launch (identity bound later at
  // IDME_INIT, inside the hook). Runs before the parent handshake starts. Must never
  // block the flow — swallow a setup failure and report it, same as SsnCardVerification.tsx.
  useEffect(() => {
    try {
      initAnalytics({ module: "legal_id", provider: "regula", locale: i18n.language });
    } catch (err) {
      logger.warn("[RegulaLegalIdVerification] analytics init failed; continuing without analytics", err);
      reportAnalyticsSetupFailure("init_failed", { module: "legal_id", error: err instanceof Error ? err.message : String(err) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The member can switch language mid-flow via the footer.
  useEffect(() => {
    setAnalyticsLocale(i18n.language);
  }, [i18n.language]);

  const regulaLocale = toRegulaLocale(i18n.language);

  // Drive the client-side Regula locator while capturing; submit the uncropped images on success.
  useRegulaEvents({
    hostRef,
    active: state === "capturing",
    locale: regulaLocale,
    translations: { [regulaLocale]: t("legalId.regula", { returnObjects: true }) as Record<string, unknown> },
    onResult: (result) => {
      logger.log("[RegulaLegalIdVerification] capture result:", { qaVerdict: result.qaVerdict });
      submitCapture({ imageFront: result.frontImageBase64, imageBack: result.backImageBase64 });
    },
    // Closing the camera ('x') exits the flow (closes the iframe via the launcher), like Veriff.
    // Clicked first so it lands before the Outcome the resulting failure emits.
    onClose: () => {
      trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.CLOSE_DOCUMENT_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE, targetAction: "navigate" });
      handleVerificationError("user_canceled", "Regula verification canceled");
    },
    onError: (err) => handleVerificationError("doc_capture_failed", err.message),
    // SDK loaded + <document-reader> mounted — drop our loading spinner; Regula's UI takes over.
    // Also the vendor-ready milestone (same concept as Veriff's MESSAGES.STARTED).
    onReady: () => {
      setSdkLoading(false);
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_READY, "indicator", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });
    },
    onCameraStarted: () => {
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.CAMERA_STREAM_STARTED, "indicator", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });
    },
    onSwitchCamera: () => {
      trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.SWITCH_CAMERA, "button", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE, targetAction: "toggle" });
    },
  });

  // Initialize on mount (validates origin, sends IDME_READY, receives the token via IDME_INIT).
  useEffect(() => {
    let cleanupFn: (() => void) | undefined;
    initializeRegulaLegalIdVerification().then((fn) => {
      cleanupFn = fn;
    });
    return () => {
      cleanupFn?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler: user clicks "Open camera" on the intro screen
  const handleOpenCamera = () => {
    logger.log("[RegulaLegalIdVerification] User clicked Open camera — starting Regula locator");
    setSdkLoading(true);
    setState("capturing");
  };

  const handleFallback = () => {
    logger.log("[RegulaLegalIdVerification] User selected the host fallback");
    handleVerificationError("user_no_id", "User requested another identity verification method");
  };

  // Handler: toggle spinner visibility on the processing screen
  const handleHideAnimation = () => {
    setShowSpinner((prev) => !prev);
  };

  // Handler: user clicks "No, retake photo" on the confirmation screen.
  // Sends retake request to parent — IVA owns retry limits and will restart the flow if allowed.
  const handleRetake = () => {
    logger.log("[RegulaLegalIdVerification] User requested retake — notifying parent");
    handleVerificationError("user_retake_requested", "User requested to retake photo of ID", undefined, { uuid: verificationResult?.data?.uuid ?? verificationId });
  };

  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(" ")}>
      <div className={styles.container}>
        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              {consumerLogo ? (
                <img src={consumerLogo.src} alt={consumerLogo.alt} />
              ) : (
                <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
              )}
            </div>
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing — loading spinner while waiting for the token */}
          {state === "initializing" && (
            <div className={styles.card}>              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — intro screen: "Take a photo of your ID" */}
          {state === "ready" && (
            <IntroScreen
              onOpenCamera={handleOpenCamera}
              onFallback={handleFallback}
              fallbackAvailable={fallbackAvailable}
              consumerLogo={consumerLogo}
              consumerConfig={consumerConfig}
            />
          )}

          {/* Capturing — Regula's <document-reader> mounts inside CaptureScreen; see there for lifecycle details. */}
          {state === "capturing" && (
            <CaptureScreen hostRef={hostRef} sdkLoading={sdkLoading} />
          )}

          {/* Processing — submitting captured images + polling backend for the decision */}
          {state === "processing" && (
            <ProcessingScreen
              showSpinner={showSpinner}
              onHideAnimation={handleHideAnimation}
            />
          )}

          {/* Confirming — show extracted ID details for user to review */}
          {state === "confirming" && verificationResult && (
            <ConfirmationScreen
              verificationResult={verificationResult}
              onConfirm={confirmVerification}
              onRetake={handleRetake}
            />
          )}

          {/* Completed */}
          {state === "completed" && (
            <div className={styles.card}>              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t("legalId.completed.title")}</h2>
              <p className={styles.subtitle}>{t("legalId.completed.subtitle")}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t("legalId.error.title")}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        {/* Hidden during capture: the Regula reader is a fixed full-viewport overlay, so a
            rendered footer would be invisible but still tabbable underneath. */}
        {state !== "capturing" && (
          <footer className={styles.footer}>
            <LanguageSelector />
            <div className={styles.footerLinks}>
              <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.whatIsIdme')}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.termsOfService')}</a>
              <span aria-hidden="true" className={styles.footerDivider}>|</span>
              <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.privacyPolicy')}</a>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
